import { each, pick, extend, defaults, omit, isString, isEmpty, has, get, find, isArray, without } from 'underscore';
import { edmCampaign } from '@bingads-webui/edm-campaign';
import {
  modelToViewModelForLocationTargets,
  modelToViewModelForDayTimeTargets,
  bulkModelToViewModelForLocationTargets,
  bulkModelToViewModelForDayTimeTargets,
  modelToViewModelForAudienceCriterions,
  modelToViewModelForPlacements,
} from './ad-group-criterions';
import { modelToViewModelForTargetCriterions } from './criterions';

import { AdGroupNegativeReferenceId } from '../common/constants';
import { modelToViewModelForDateSetting, viewModelToBulkModelForDateSetting, bulkModelToViewModelForDateSetting } from '../common/date-settings';

const whitelistedSchemaPropertyKeysForModel = [
  'Id', 'Name', 'Status', 'BidStrategyName', 'Campaign', 'CpmBid', 'CpvBid', 'SearchBid',
  'AdScheduleUseSearcherTimeZone', 'StartDate', 'EndDate', 'FrequencyCapSettings',
  'TrackingUrlTemplate', 'FinalUrlSuffix', 'UrlCustomParameters',
];

const readonlyPropertyKeysForModel = [
  'StartDate',
];

export const modelToViewModelForAdGroup = ({
  adGroupModel,
  campaignModel,
  locationCriterionsModel,
  campaignLocationCriterionsModel,
  campaignDeviceCriterions,
  dayTimeCriterionsModel,
  bundleCountries,
  targetCriterionsModel,
  audienceCriterionsModel,
  placementCriterionsModel,
  i18n,
  isAudienceAds = false,
  isAudienceAdGroupEdit = false,
  isDisplayAds = false,
  isVideoAds = false,
  defaultAdGroupData = null,
  defaultTargeting = null,
  isXandrServing = false,
  separateIMA = false,
  campaignExclusions,
  adgroupExclusions,
  ads,
}) => {
  let data = pick(adGroupModel, whitelistedSchemaPropertyKeysForModel);

  if (isAudienceAds) {
    const AdRotationSettings = get(adGroupModel, 'AdRotation', {}) || {};
    data.AdRotation = AdRotationSettings.RotationType;
  }

  if (campaignExclusions || adgroupExclusions) {
    data.exclusions = {
      campaignExclusions,
      adgroupExclusions,
    };
  }

  if (isAudienceAds ||
    (!isXandrServing && isDisplayAds) ||
    (!isXandrServing && isVideoAds)) {
    if (adGroupModel.UseOptimizedTargeting != null) {
      data.UseOptimizedTargeting = adGroupModel.UseOptimizedTargeting;
    }

    /**
      * NOTE: this is where the default values from getDefaultAdGroup() - are gettting over-written by the value from the adGroupModel odata response.
      * MT odata response for AdGroup can have values missing or different from getDefaultAdGroup()
      * example: responds with a default value (0.05) for SearchBid, even when it was NOT set by the Bulk Upsert call.
     */

    data.CampaignSubType = campaignModel && campaignModel.CampaignSubType;
    data.CampaignBidStrategyName = campaignModel && campaignModel.BiddingScheme.Type;

    const adGroupSettings = get(adGroupModel, 'Settings', []);

    const BoostPlacementSetting = find(adGroupSettings, setting => setting['@odata.type'] === '#Model.BoostPlacementSetting') || {};

    data.BoostAdunitIds = BoostPlacementSetting.AdUnitIds;
    data.BoostPublisherIds = BoostPlacementSetting.PublisherIds;
    data.BoostAccountIds = BoostPlacementSetting.AccountIds;
    data.BoostTriggerIds = BoostPlacementSetting.TriggerIds;

    const adGroupTargetSettings = get(adGroupModel, 'TargetSetting', null);
    if (adGroupTargetSettings) {
      each(adGroupTargetSettings.Details, (targetSetting) => {
        data[`isTargetSet${targetSetting.CriterionType}`] = !targetSetting.TargetAll;
      });
    }
  }

  if (placementCriterionsModel) {
    data = extend(data, modelToViewModelForPlacements({ model: placementCriterionsModel }));
  }

  if (locationCriterionsModel) {
    data = extend(data, modelToViewModelForLocationTargets({
      model: locationCriterionsModel,
      campaignLocationModel: campaignLocationCriterionsModel,
      bundleCountries,
      adGroupId: adGroupModel.Id,
      campaignId: campaignModel && campaignModel.Id,
      bidMode: isAudienceAdGroupEdit,
    }));
  }

  if (dayTimeCriterionsModel) {
    data = extend(data, modelToViewModelForDayTimeTargets({
      model: dayTimeCriterionsModel,
    }));
  }

  if (adGroupModel.StartDate) {
    data = extend(data, modelToViewModelForDateSetting({
      model: adGroupModel,
      i18n,
    }));
  }

  if (targetCriterionsModel) {
    data = extend(data, modelToViewModelForTargetCriterions({
      model: targetCriterionsModel,
      campaignDeviceCriterions,
    }));
  }

  if ((targetCriterionsModel === null ||
    (targetCriterionsModel && isArray(targetCriterionsModel) && targetCriterionsModel.length === 0)) &&
    defaultTargeting) {
    data = extend(data, defaultTargeting);
  }

  if (audienceCriterionsModel) {
    data = extend(data, modelToViewModelForAudienceCriterions({
      model: audienceCriterionsModel,
      separateIMA,
    }));
  }

  if (defaultAdGroupData) {
    defaults(data, defaultAdGroupData);
  }

  data.ads = get(ads, 'ads', {});

  const adGroupViewModel = {
    data,
    isValid: true,
    isDirty: false,
  };
  return adGroupViewModel;
};

export const bulkModelToViewModelForAdGroup = ({
  adGroupModel,
  bundleCountries,
  originalAdGroupViewModel,
  i18n,
}) => {
  let data = pick(adGroupModel, whitelistedSchemaPropertyKeysForModel);
  if (data.Id && isString(data.Id)) {
    // eslint-disable-next-line no-param-reassign
    data.Id = parseInt(data.Id, 10);
  }
  data = extend(data, bulkModelToViewModelForLocationTargets({
    locationTargetsModel: adGroupModel.AdGroupCriterions,
    bundleCountries,
  }));
  data = extend(data, bulkModelToViewModelForDayTimeTargets({
    criterions: adGroupModel.AdGroupCriterions,
  }));

  data = extend(data, bulkModelToViewModelForDateSetting({ model: adGroupModel, i18n }));

  if (isEmpty(data.FrequencyCapSettings)) {
    data = extend(data, { FrequencyCapSettings: [{}] });
  }

  if (originalAdGroupViewModel.data.customSegments) {
    data = extend(data, { customSegments: originalAdGroupViewModel.data.customSegments });
    data = extend(data, { initialCustomSegments: originalAdGroupViewModel.data.initialCustomSegments });
  }

  if (originalAdGroupViewModel.data.deal) {
    data = extend(data, {
      deal: originalAdGroupViewModel.data.deal,
    });
  }

  if (has(adGroupModel, 'AdRotation')) {
    data = extend(data, { AdRotation: adGroupModel.AdRotation });
  }

  const adGroupViewModel = {
    data,
    isValid: true,
    isDirty: false,
  };
  return adGroupViewModel;
};

export const viewModelToModelForAdGroup = ({ adGroupViewModel, i18n, isDisplayPlus = false }) => {
  let model = pick(adGroupViewModel.data, whitelistedSchemaPropertyKeysForModel);

  model = extend(model, viewModelToBulkModelForDateSetting({ i18n, viewModel: adGroupViewModel }));

  if (!adGroupViewModel.data.Id || adGroupViewModel.data.Id === AdGroupNegativeReferenceId) {
    model.Id = AdGroupNegativeReferenceId;

    if (isEmpty(model.FrequencyCapSettings) ||
      isEmpty(model.FrequencyCapSettings[0]) ||
      !model.FrequencyCapSettings[0].CapValue) {
      model = omit(model, 'FrequencyCapSettings');
    }
  } else if (isDisplayPlus) {
    const filteredReadonlyKeys = without(readonlyPropertyKeysForModel, 'StartDate');
    model = omit(model, filteredReadonlyKeys);
  } else {
    model = omit(model, readonlyPropertyKeysForModel);
  }

  if (has(adGroupViewModel.data, 'UseOptimizedTargeting') && adGroupViewModel.data.UseOptimizedTargeting !== null) {
    model.UseOptimizedTargeting = adGroupViewModel.data.UseOptimizedTargeting;
  }

  if (has(adGroupViewModel.data, 'PricingModel')) {
    model.PricingModel = adGroupViewModel.data.PricingModel;
  }

  if (has(adGroupViewModel.data, 'BoostAdunitIds')) {
    const adUnitIds = adGroupViewModel.data.BoostAdunitIds;
    model.BoostAdunitIds = isArray(adUnitIds) ? adUnitIds.join(';') : adUnitIds;
    model.BoostPublisherIds = adGroupViewModel.data.BoostPublisherIds;
    model.BoostAccountIds = adGroupViewModel.data.BoostAccountIds;
    model.BoostTriggerIds = adGroupViewModel.data.BoostTriggerIds;
  }

  if (has(adGroupViewModel.data, 'TargetSetting') && adGroupViewModel.data.TargetSetting !== null) {
    model.TargetSetting = adGroupViewModel.data.TargetSetting;
  }

  if (has(adGroupViewModel.data, 'AdRotation')) {
    model.AdRotation = adGroupViewModel.data.AdRotation;
  }

  return model;
};


const readAdGroup = (cid, aid, campaignId, adGroupId) =>
  edmCampaign
    .Customers.$withKey(cid)
    .Accounts.$withKey(aid)
    .Campaigns.$withKey(campaignId)
    .AdGroups.$withKey(adGroupId)
    .get()
    .toPromise();

export const loadAdGroup = (cid, aid, campaignId, adGroupId) =>
  readAdGroup(cid, aid, campaignId, adGroupId);
